import React, { useState } from "react";
import "../../styles/EditModal.css";
import { toast } from "react-toastify";

const EditModal = ({ onClose, onSave, count }) => {
  const contactFields = [
    "First Name",
    "Last Name",
    "Phone",
    "Email",
    "Company",
    "Job Title",
    "Department",
    "Lead Status",
    "Lead Source",
    "LinkedIn URL",
  ];

  const alphabetFields = ["First Name", "Last Name", "Company", "Job Title"];

  const fieldMap = {
    "First Name": "firstName",
    "Last Name": "lastName",
    Phone: "phone",
    Email: "email",
    Company: "companyName",
    "Job Title": "jobTitle",
    Department: "department",
    "Lead Status": "leadStatus",
    "Lead Source": "leadSource",
    "LinkedIn URL": "linkedInUrl",
  };

  // Department (unchanged - custom)
  const departmentOptions = [
    { value: "EXECUTIVE_LEADERSHIP", label: "Executive Leadership" },
    { value: "HUMAN_RESOURCES", label: "Human Resources" },
    { value: "TALENT_ACQUISITION", label: "Talent Acquisition" },
    { value: "SALES", label: "Sales" },
    { value: "MARKETING", label: "Marketing" },
    { value: "CUSTOMER_SUPPORT", label: "Customer Support" },
    { value: "FINANCE", label: "Finance" },
    { value: "LEGAL", label: "Legal" },
    { value: "IT", label: "IT" },
    { value: "ENGINEERING", label: "Engineering" },
    { value: "PRODUCT_MANAGEMENT", label: "Product Management" },
    { value: "DESIGN_UX", label: "Design & UX" },
    { value: "OPERATIONS", label: "Operations" },
    { value: "BUSINESS_DEVELOPMENT", label: "Business Development" },
    { value: "PROCUREMENT", label: "Procurement" },
    { value: "MANUFACTURING", label: "Manufacturing" },
    { value: "LOGISTICS", label: "Logistics" },
    { value: "QUALITY_ASSURANCE", label: "Quality Assurance" },
    { value: "RESEARCH_DEVELOPMENT", label: "Research & Development" },
    { value: "TRAINING_LD", label: "Training & L&D" },
    { value: "SECURITY_RISK", label: "Security & Risk" },
    { value: "PR_COMMUNICATIONS", label: "PR & Communications" },
    { value: "FACILITIES_MANAGEMENT", label: "Facilities Management" },
    { value: "CONSULTING", label: "Consulting" },
    { value: "HEALTHCARE", label: "Healthcare" },
    { value: "EDUCATION", label: "Education" },
    { value: "DATA_ANALYTICS", label: "Data & Analytics" },
    { value: "INVESTOR_RELATIONS", label: "Investor Relations" },
    { value: "CORPORATE_STRATEGY", label: "Corporate Strategy" },
    { value: "OTHER", label: "Other" },
  ];

  // ✅ Backend aligned
  const leadStatusOptions = [
    { value: "NEW", label: "New" },
    { value: "CONTACTED", label: "Contacted" },
    { value: "QUALIFIED", label: "Qualified" },
    { value: "WON", label: "Won" },
    { value: "LOST", label: "Lost" },
    { value: "OTHER", label: "Other" },
  ];

  // ✅ Backend aligned
  const leadSourceOptions = [
    { value: "WEBSITE", label: "Website" },
    { value: "REFERRAL", label: "Referral" },
    { value: "PARTNER", label: "Partner" },
    { value: "SOCIAL_MEDIA", label: "Social Media" },
    { value: "EMAIL", label: "Email" },
    { value: "CALL", label: "Call" },
    { value: "OTHER", label: "Other" },
  ];

  const [property, setProperty] = useState("First Name");
  const [value, setValue] = useState("");

  const handlePropertyChange = (e) => {
    setProperty(e.target.value);
    setValue("");
  };

  const handleChange = (e) => {
    let val = e.target.value;

    if (property === "Phone") {
      val = val.replace(/\D/g, "");
      if (val.length === 12 && val.startsWith("91")) val = val.slice(2);
      if (val.length === 11 && val.startsWith("0")) val = val.slice(1);
      if (val.length > 10) val = val.slice(0, 10);
    } else if (alphabetFields.includes(property)) {
      val = val.replace(/[^a-zA-Z\s]/g, "");
    }

    setValue(val);
  };

  const handleUpdate = () => {
    if (!value) {
      toast.warning("Please enter a value to update");
      return;
    }

    if (property === "Phone" && value.length !== 10) {
      toast.warning("Phone number must be exactly 10 digits");
      return;
    }

    if (property === "Email" && !/^\S+@\S+\.\S+$/.test(value)) {
      toast.warning("Invalid email format");
      return;
    }

    if (
      property === "LinkedIn URL" &&
      !/^https?:\/\/(www\.)?linkedin\.com\/.+$/.test(value)
    ) {
      toast.warning("Invalid LinkedIn URL");
      return;
    }

    const fieldName = fieldMap[property];
    if (!fieldName) return;

    onSave(fieldName, value);
    onClose();
  };

  const handleCancel = () => {
    setProperty("First Name");
    setValue("");
  };

  const renderInput = () => {
    if (property === "Department") {
      return (
        <select value={value} onChange={(e) => setValue(e.target.value)}>
          <option value="">Select department</option>
          {departmentOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    }

    if (property === "Lead Status") {
      return (
        <select value={value} onChange={(e) => setValue(e.target.value)}>
          <option value="">Select lead status</option>
          {leadStatusOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    }

    if (property === "Lead Source") {
      return (
        <select value={value} onChange={(e) => setValue(e.target.value)}>
          <option value="">Select lead source</option>
          {leadSourceOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    }

    if (property === "LinkedIn URL") {
      return (
        <input
          type="url"
          placeholder="Enter new LinkedIn URL"
          value={value}
          onChange={handleChange}
        />
      );
    }

    if (property === "Phone") {
      return (
        <input
          type="tel"
          placeholder="Enter new Phone"
          value={value}
          onChange={handleChange}
        />
      );
    }

    return (
      <input
        type="text"
        placeholder={`Enter new ${property}`}
        value={value}
        onChange={handleChange}
      />
    );
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content edit-modal">
        <div className="modal-header">
          <h2>
            Bulk edit {count} {count === 1 ? "record" : "records"}
          </h2>
          <button className="close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          <label>Property to update</label>
          <select value={property} onChange={handlePropertyChange}>
            {contactFields.map((field) => (
              <option key={field}>{field}</option>
            ))}
          </select>

          <label style={{ marginTop: "10px" }}>{property}</label>
          {renderInput()}
        </div>

        <div className="modal-footer">
          <button className="update-btn" onClick={handleUpdate}>
            Update
          </button>
          <button className="cancel-btn" onClick={handleCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditModal;